import { Bounds, Feature, FeatureCollection, FeatureCollection as MyFC, Feature as MyFeature, Geometry as MyGeometry } from "../../shared/types/MapShapes";
import { convertValueToNumber, getUniqueId } from './';
import { flatten } from '@turf/turf';
export const initMapCenter: any = {
    lat: -32.818798,
    lng: -55.948865
};

export const convertToGeoJSON = (polygons) => {
    // Crear la estructura inicial de FeatureCollection
    const geoJSON: FeatureCollection = {
        type: "FeatureCollection",
        features: [],
    };

    // Iterar sobre cada feature
    polygons.forEach((feature) => {
        // Usar el método para obtener las coordenadas
        const coordinatesList = getCoordinatesFromFeature(feature);

        // Recorrer cada grupo de coordenadas y convertirlo en un Feature de GeoJSON
        coordinatesList.forEach((multiPolygon) => {
            const geometry: any = {
                type: "MultiPolygon", // Por defecto asumimos MultiPolygon
                coordinates: [],
            };

            // Si es un MultiPolygon, agregamos todos los polígonos
            multiPolygon.forEach((polygonCoordinates) => {
                geometry.coordinates.push(polygonCoordinates.map((ring) =>
                    ring.map((coordinate) => [coordinate.lng, coordinate.lat])
                ));
            });

            // Cambiamos a Polygon si solo tiene un conjunto de coordenadas
            if (geometry.coordinates.length === 1) {
                geometry.type = "Polygon";
                geometry.coordinates = geometry.coordinates[0];
            }

            // Agregar el Feature al FeatureCollection
            let feature = {
                type: "Feature",
                geometry: geometry,
                properties: {}, // Agrega propiedades si es necesario
            };
            geoJSON.features.push(feature);
        });
    });

    return geoJSON;
}

export const convertToSexagesimal = (decimal: number, isLatitude: boolean) => {
    const absolute = Math.abs(decimal);
    const degrees = Math.floor(absolute);
    const minutes = Math.floor((absolute - degrees) * 60);
    const seconds = ((absolute - degrees) * 60 - minutes) * 60;

    const direction = isLatitude
      ? (decimal >= 0 ? "N" : "S")
      : (decimal >= 0 ? "E" : "W");

    return {
      direction,
      degrees,
      minutes,
      seconds: parseFloat(seconds.toFixed(3)), // Redondeo a 3 decimales
    };
  }

export const convertDecimalListToSexagesimal = (rings: google.maps.LatLngLiteral[][]) => {
    const coords = rings.flatMap((ring, ringIndex) =>
        ring.map((item, index) => ({
        latitude: convertToSexagesimal(convertValueToNumber(item.lat) as number, true),
        longitude: convertToSexagesimal(convertValueToNumber(item.lng) as number, false),
        isEditing: false,
        id: `${ringIndex + 1}-${index + 1}`,
        ringIndex
        }))
    );
    return coords;
};

export const polygonsAreValid = (polygonList:google.maps.Polygon[]) => {
    for (let i = 0; i < polygonList.length; i++) {
        const polygon = polygonList[i];

        //Checkeo si el polígono tiene al menos 3 vértices
        if (polygon.getPath().getLength() < 3) {
            return false;
        }
    }
    return true; 
}

interface CustomPinElement {
    scale?: number;
    backgroundColor: string;
    borderColor?: string;
    icon?: {
        glyph: HTMLElement,
        glyphColor?: string
    } | null
}

export const customPinElement = (
    {
        scale = 1,
        backgroundColor = '',
        borderColor = '',
        icon = null
    }: CustomPinElement
):google.maps.marker.PinElement["element"] => {
    let pin = new google.maps.marker.PinElement();
    pin.scale = scale;
    pin.background = backgroundColor;
    if(icon){
        pin.glyph = icon.glyph;
        pin.glyphColor = icon.glyphColor
    }
    if (borderColor) {
        pin.borderColor = borderColor;
    }
    return pin.element;
}

export const customIconForMarker = ({
    glyph: glyphIcon, //'<i class="fa fa-pizza-slice fa-lg"></i>',
    glyphColor = null
     
} : {
    glyph: string, 
    glyphColor?: string | null
}) => {
    const icon = document.createElement('div') as HTMLElement;
    icon.innerHTML = glyphIcon
    return {
        glyph: icon,
        glyphColor: glyphColor || 'black'
    };
}

export const customImageForMarker = (
    imgUrl: string,
):HTMLImageElement => {
    /* Agregar una imagen al "content" de un marker */
    const glyphImg = document.createElement('img');
    glyphImg.src = imgUrl;
    return glyphImg
}

export const customSvgForMarker = (
    svgString: string,
): HTMLElement => {
    const parser = new DOMParser();
    const pinSvg = parser.parseFromString(svgString, 'image/svg+xml').documentElement;
    return pinSvg;
}

export function imOverPolyVertex(
  event: google.maps.Data.MouseEvent,
  tolerance = 10
): boolean {
  const latLngClicked = event.latLng as google.maps.LatLng;
  const feature = event.feature!;
  const geometry = feature.getGeometry();

  let polygons: google.maps.Data.Polygon[] = [];

  if (geometry instanceof google.maps.Data.MultiPolygon) {
    polygons = geometry.getArray();
  } else if (geometry instanceof google.maps.Data.Polygon) {
    polygons = [geometry];
  }

  // Iterar cada polígono (o cada anillo si es MultiPolygon)
  for (let i = 0; i < polygons.length; i++) {
    const polygon = polygons[i];
    const paths = polygon.getArray(); // array de LinearRing

    for (let j = 0; j < paths.length; j++) {
      const path = paths[j];
      const coords = path.getArray(); // array de LatLng (los vértices)

      for (let k = 0; k < coords.length; k++) {
        const vertex = coords[k];
        const distance = google.maps.geometry.spherical.computeDistanceBetween(
          latLngClicked,
          vertex
        );

        if (distance < tolerance) {
          return true;
        }
      }
    }
  }
  return false;
}

export const getExtendedBoundsFromFeature = (
    feature: google.maps.Data.Feature,
    bounds: google.maps.LatLngBounds | null = null 
): google.maps.LatLngBounds => {
    let featureBounds = bounds || new google.maps.LatLngBounds();
    let geometry = feature.getGeometry();
    let polygons: google.maps.Data.Polygon[] = [];

    if (geometry instanceof google.maps.Data.MultiPolygon) {
        polygons = geometry.getArray();
    } else if (geometry instanceof google.maps.Data.Polygon) {
        polygons = [geometry];
    }

    polygons.forEach((polygon: google.maps.Data.Polygon) => {
        polygon.getArray().forEach((linearRing) => {
            linearRing.getArray().forEach((latLng) => {
                featureBounds.extend(latLng);
            });
        });
    });

    return featureBounds;
}

export const extendBoundsPolygon  = (polygon: google.maps.Data.Polygon, bounds: google.maps.LatLngBounds): google.maps.LatLngBounds => {
    polygon.getArray().forEach((linearRing) => {
        linearRing.getArray().forEach((latLng) => {
            bounds.extend(latLng);
        });
    });

    return bounds
}

export function removeVertexFromFeature(event: google.maps.Data.MouseEvent): google.maps.Data.Feature {
    const feature = event.feature;
    const geometry = feature.getGeometry();
    const userClickLatLng = event.latLng as google.maps.LatLng;

    let polygons: google.maps.Data.Polygon[] = [];
    const newPolygons: google.maps.Data.Polygon[] = [];

    // Identificar si la geometría es MultiPolygon o Polygon
    if (geometry instanceof google.maps.Data.MultiPolygon) {
        polygons = geometry.getArray();
    } else if (geometry instanceof google.maps.Data.Polygon) {
        polygons = [geometry];
    }

    polygons.forEach((polygon) => {
        const paths = polygon.getArray();
        const updatedPaths: google.maps.Data.LinearRing[] = [];

        paths.forEach((path) => {
            const pathCoords = path.getArray();
            const newLatLngCoordsForCurrentPath: google.maps.LatLng[] = [];

            // Recorremos cada vértice para verificar si debe eliminarse
            for (let j = 0; j < pathCoords.length; j++) {
                const vertex = pathCoords[j];
                const distance = google.maps.geometry.spherical.computeDistanceBetween(userClickLatLng, vertex);

                if (distance > 10) {
                    newLatLngCoordsForCurrentPath.push(vertex);
                }
            }

            // Verificamos que el camino resultante tenga al menos 3 vértices
            if (newLatLngCoordsForCurrentPath.length >= 3) {
                updatedPaths.push(new google.maps.Data.LinearRing(newLatLngCoordsForCurrentPath));
            }
        });

        // Si el polígono tiene al menos un camino válido, lo agregamos
        if (updatedPaths.length > 0) {
            newPolygons.push(new google.maps.Data.Polygon(updatedPaths));
        }
    });

    // Reasignamos la geometría a la característica
    if (newPolygons.length > 1) {
        feature.setGeometry(new google.maps.Data.MultiPolygon(newPolygons));
    } else if (newPolygons.length === 1) {
        feature.setGeometry(newPolygons[0]);
    } else {
        // Si no hay polígonos válidos, podrías optar por eliminar la característica
        console.warn("No hay polígonos válidos después de eliminar los vértices.");
        // O simplemente eliminar la geometría:
        feature.setGeometry(null);
    }

    return feature;
}


export function parseGeoJsonToGoogleMapsGeometry(geojsonGeometry) {
    switch (geojsonGeometry.type) {
      case 'Point':
        return new google.maps.LatLng(geojsonGeometry.coordinates[1], geojsonGeometry.coordinates[0]);
  
      case 'LineString':
        return geojsonGeometry.coordinates.map(coord => new google.maps.LatLng(coord[1], coord[0]));
  
      case 'Polygon':
        return geojsonGeometry.coordinates.map(ring => ring.map(coord => new google.maps.LatLng(coord[1], coord[0])));
  
      case 'MultiPolygon':
        return geojsonGeometry.coordinates.map(polygon => {
          return polygon.map(ring => {
            return ring.map(coord => new google.maps.LatLng(coord[1], coord[0]));
          });
        });
  
      // Handle other geometry types as needed
  
      default:
        console.error('Unsupported geometry type:', geojsonGeometry.type);
        return null;
    }
  }

  export function ensureOrientation(coordinates: google.maps.LatLng[], clockwise: boolean): google.maps.LatLng[] {
    const area = google.maps.geometry.spherical.computeSignedArea(coordinates);
    const isClockwise = area < 0; // Área negativa indica orientación en sentido horario
    if ((clockwise && !isClockwise) || (!clockwise && isClockwise)) {
        return coordinates.reverse(); // Invierte la orientación si no coincide
    }
    return coordinates;
}


export function getNewFeatureGeometryWithNewPolygons(
    newPolygons: google.maps.Polygon[],
    feature: google.maps.Data.Feature
): google.maps.Data.MultiPolygon {
    
    let geometry = feature.getGeometry();
    let polygons: google.maps.Data.Polygon[] = [];
    
    // feature.toGeoJson((geojson) => {
    //     console.log("Initial GeoJSON:", geojson);
    // });


    if (geometry instanceof google.maps.Data.MultiPolygon) {
        polygons = geometry.getArray();
    } else if (geometry instanceof google.maps.Data.Polygon) {
        polygons = [geometry];
    }

    let newPolygonsData: google.maps.Data.Polygon[] = [...polygons];


    // Procesar cada nuevo polígono
    newPolygons.forEach((newPolygon: google.maps.Polygon) => {
        // Obtener los caminos del nuevo polígono
        const paths: google.maps.Data.LinearRing[] = newPolygon.getPaths().getArray().map((path: google.maps.MVCArray<google.maps.LatLng>) => {
            const adjustedPath = ensureOrientation(path.getArray(), false); // Ajustar orientación como agujero
            return new google.maps.Data.LinearRing(adjustedPath);
        });

        // Crear un nuevo google.maps.Data.Polygon con los caminos
        const newPolygonData = new google.maps.Data.Polygon(paths);

        let isHole = false;

        // Verificar cada polígono existente
        for (let index = 0; index < newPolygonsData.length; index++) {
            const existingPolygon = newPolygonsData[index];
            const currentPoly = new google.maps.Polygon({
                paths: existingPolygon.getArray().map((ring: google.maps.Data.LinearRing) => {
                    return ring.getArray();
                }),
                fillOpacity: 0.1,
                fillColor: "#000000"
            });

            // Verificar si el nuevo polígono es un agujero dentro de un polígono existente
            const points = paths[0].getArray();
            if (points.some(point => google.maps.geometry.poly.containsLocation(point, currentPoly))) {
                isHole = true;
                // Actualizar el polígono existente con el nuevo agujero
                const updatedPaths = existingPolygon.getArray();
                updatedPaths.push(paths[0]); // Agregar el agujero
                newPolygonsData[index] = new google.maps.Data.Polygon(updatedPaths);
                break;
            }
        }

        if (!isHole) {
            newPolygonsData.push(newPolygonData);
        }
    });

    // Reconstruir la geometría con los polígonos modificados
    let geometryParsed: google.maps.Data.MultiPolygon;

    if (newPolygonsData.length > 1) {
        geometryParsed = new google.maps.Data.MultiPolygon(newPolygonsData);
    } else if (newPolygonsData.length === 1) {
        geometryParsed = new google.maps.Data.MultiPolygon([newPolygonsData[0]]);
    } else {
        throw new Error("No polygons available to create geometry");
    }

    feature.setGeometry(geometryParsed);

    // // Validar el GeoJSON resultante
    // feature.toGeoJson((geojson) => {
    //     console.log("Updated GeoJSON:", geojson);
    // });

    return geometryParsed;
}


export function convertCoordinatesToLatLng(coords: google.maps.LatLngLiteral[][][][]):Array<google.maps.LatLng> {
    const result:Array<google.maps.LatLng> = [];

    coords.forEach(coord1 => {
        coord1.forEach(coord2 => {
            coord2.forEach(coord3 => {
                coord3.forEach(latLng => {
                    result.push(new google.maps.LatLng(latLng.lat, latLng.lng));
                });
                
            });
        });
    });
    return result;
}

// Extract coordinates from the new polygon
export const getCoordinatesFromPolygon = (newPolygon: google.maps.Polygon): google.maps.LatLng[] => {
    let newCoords:google.maps.LatLng[] = [];
    let paths = newPolygon.getPaths();
    paths.forEach(path => {
        let coords = path.getArray();
        coords.forEach(coord => {
            newCoords.push(coord);
        });
    });
    return newCoords;
}


export const hideMarkerContent = (marker: google.maps.marker.AdvancedMarkerElement) => {
    toggleVisibleMarkerContent(marker, false);
}

export const toggleVisibleMarkerContent = (
    marker: google.maps.marker.AdvancedMarkerElement,
    visible?: boolean
) => {
    if (marker && marker.content) {
        const element = marker.content as HTMLElement;
        element.style.display = visible ? 'block' : 'none';
    }
}

export const removeMarkerFromMap = (marker: google.maps.marker.AdvancedMarkerElement) => {
    if (marker) {
        marker.map = null;
    }
}

/**
 * Convierte las coordenadas de un FeatureCollection (cualquiera de:
 *   - Features de tipo "MultiPolygon"
 *   - Features de tipo "Polygon"
 * ) a PolyCoords (google.maps.LatLngLiteral[][][][])
 *
 * @param featureCollection - Objeto GeoJSON de tipo FeatureCollection
 * @returns google.maps.LatLngLiteral[][][][]  (formato que espera backend al enviar un polígono)
 */
export function extractPolyCoords(featureCollection) {
  if (!featureCollection || featureCollection.type !== "FeatureCollection") {
    throw new Error("El objeto debe ser un FeatureCollection válido.");
  }

  return featureCollection.features.map(feature => {
    if (!feature.geometry || !feature.geometry.type) {
      throw new Error("El feature no tiene geometría o no es válida");
    }

    if (feature.geometry.type === "MultiPolygon") {
      const multiCoords = feature.geometry.coordinates as number[][][][];

      return multiCoords.map(polygon =>
        polygon.map(ring =>
          ring.map(([lng, lat]) => ({ lat, lng }))
        )
      );
    }

    if (feature.geometry.type === "Polygon") {
      const polyCoords = feature.geometry.coordinates as number[][][];

      return [
        polyCoords.map(ring =>
          ring.map(([lng, lat]) => ({ lat, lng }))
        )
      ];
    }

    throw new Error(
      `Solo se admiten geometrías de tipo MultiPolygon o Polygon. Encontrado: ${feature.geometry.type}`
    );
  });
}


/**
 * Convierte coordenadas sexagesimales a decimales.
 * @param {string} direction - La dirección (N, S, E, W).
 * @param {number} degrees - Los grados.  
 * @param {number} minutes - Los minutos.
 * @param {number} seconds - Los segundos.
 * @returns {number} - Los grados decimales.
 */
export const convertToDecimal = (direction: string, degrees: number, minutes: number, seconds: number): number => {
    const deg = parseFloat(degrees.toString());
    const min = parseFloat(minutes.toString());
    const sec = parseFloat(seconds.toString());
  
    const decimal = deg + min / 60 + sec / 3600;
  
    // Devuelve el valor negativo si la dirección es "S" o "W"
    return direction === "S" || direction === "W" ? -decimal : decimal;
  }


export const validateCoordinate = (value: number, min: number, max: number): boolean => {
    return value >= min && value <= max;
}




export function getCoordinatesFromFeature(feature: google.maps.Data.Feature): google.maps.LatLngLiteral[][][][] {
    const coordinatesList: google.maps.LatLngLiteral[][][][] = [];

    // Get the geometry of the feature
    const geometry = feature.getGeometry() as google.maps.Data.Geometry;

    const geometryType = geometry.getType();


    // Check if the geometry is a MultiPolygon
    if (geometry.getType() === 'MultiPolygon' && geometry instanceof google.maps.Data.MultiPolygon) {
        // Iterate over each polygon in the MultiPolygon
        geometry.getArray().forEach((polygon: google.maps.Data.Polygon) => {
            const polygonCoordinates: google.maps.LatLngLiteral[][] = [];

            // Iterate over each linear ring in the polygon (outer ring and inner rings)
            polygon.getArray().forEach((linearRing: google.maps.Data.LinearRing) => {
                const ringCoordinates: google.maps.LatLngLiteral[] = [];

                // Iterate over each coordinate in the linear ring
                linearRing.getArray().forEach((latLng: google.maps.LatLng) => {
                    const coordinate: google.maps.LatLngLiteral = {
                        lat: latLng.lat(),
                        lng: latLng.lng()
                    };

                    // Check if the coordinate already exists in the ringCoordinates array
                    if (!ringCoordinates.some(coord => coord.lat === coordinate.lat && coord.lng === coordinate.lng)) {
                        ringCoordinates.push(coordinate);
                    }
                });

                // Add the ring coordinates to the polygon coordinates
                polygonCoordinates.push(ringCoordinates);
            });

            //if coordinates are not empty
            if (polygonCoordinates.length > 0) {
                coordinatesList.push([polygonCoordinates]);
            }
        });
    }

    if (geometry.getType() === 'Polygon' && geometry instanceof google.maps.Data.Polygon) {
        const polygonCoordinates: google.maps.LatLngLiteral[][] = [];
        // Iterate over each linear ring in the polygon (outer ring and inner rings)
        geometry.getArray().forEach((linearRing: google.maps.Data.LinearRing) => {
            const ringCoordinates: google.maps.LatLngLiteral[] = [];

            // Iterate over each coordinate in the linear ring
            linearRing.getArray().forEach((latLng: google.maps.LatLng) => {
                ringCoordinates.push({ lat: latLng.lat(), lng: latLng.lng() });
            });

            // Add the ring coordinates to the polygon coordinates
            polygonCoordinates.push(ringCoordinates);
        });

        // Add the polygon coordinates to the coordinates list
        coordinatesList.push([polygonCoordinates]);
    }

    return coordinatesList;
}

export const normalizeToMultiPolygon = (feature: google.maps.Data.Feature): google.maps.Data.MultiPolygon => {
    const geometry = feature.getGeometry();
    if (!geometry) throw new Error('Feature does not have geometry.');;
  
    if (geometry.getType() === 'Polygon') {
      const polygon = geometry as google.maps.Data.Polygon;
      const rings = polygon.getArray();
      return new google.maps.Data.MultiPolygon([rings]);
    }
  
    return geometry as google.maps.Data.MultiPolygon;
  }


  export function flattenPolygonCoordinates(
    coords: google.maps.LatLngLiteral[][][][]
  ): google.maps.LatLngLiteral[] {
    if (
      coords.length > 0 &&
      coords[0].length > 0 &&
      coords[0][0].length > 0 &&
      coords[0][0][0].length > 0
    ) {
      return coords[0][0][0];
    }
    return [];
  }

  export function normalizePolygonCoords(coords: google.maps.LatLngLiteral[]): google.maps.LatLngLiteral[] {
    const normalized = [...coords];
  
    // Asegurar cierre del polígono
    const first = coords[0];
    const last = coords[coords.length - 1];
    if (first.lat !== last.lat || first.lng !== last.lng) {
      normalized.push({ lat: first.lat, lng: first.lng });
    }
  
    // Asegurar orientación antihoraria
    const latLngArray = normalized.map(c => new google.maps.LatLng(c.lat, c.lng));
    const area = google.maps.geometry.spherical.computeSignedArea(latLngArray);
  
    if (area < 0) {
      normalized.reverse();
    }
  
    return normalized;
  }




/**
 * Crea un marcador (AdvancedMarkerElement) con número para cada polígono,
 * recibiendo la geometría ya sea tipo MultiPolygon o Polygon.
 *
 * @param feature           – el feature de google.maps.Data.Feature.
 * @param googleMapInstance – la instancia de google.maps.Map donde pintar el label.
 * @param markersPositions  – array donde se van guardando todos los AdvancedMarkerElement ya creados.
 * @param startingIndex     – índice inicial (entero) para numerar los próximos polígonos.
 *
 * @returns el siguiente índice disponible después de procesar esta feature.
 */
export function createPolygonLabels(
  feature: google.maps.Data.Feature,
  googleMapInstance: google.maps.Map,
  markersPositions: google.maps.marker.AdvancedMarkerElement[],
  startingIndex: number
): number {
  const geometry = feature.getGeometry() as google.maps.Data.MultiPolygon | google.maps.Data.Polygon;
  if (!geometry) return startingIndex;

  if (geometry.getType() === 'MultiPolygon') {
    const polygons = (geometry as google.maps.Data.MultiPolygon).getArray();
    polygons.forEach(polygon => {
      const bounds = new google.maps.LatLngBounds();
      polygon.getArray().forEach(linearRing =>
        linearRing.getArray().forEach(latLng => bounds.extend(latLng))
      );
      const center = bounds.getCenter();
      startingIndex = createLabelMarker(center, startingIndex, googleMapInstance, markersPositions);
    });
    return startingIndex;
  }

  if (geometry.getType() === 'Polygon') {
    const polygon = geometry as google.maps.Data.Polygon;
    const bounds = new google.maps.LatLngBounds();
    polygon.getArray().forEach(linearRing =>
      linearRing.getArray().forEach(latLng => bounds.extend(latLng))
    );
    const center = bounds.getCenter();
    return createLabelMarker(center, startingIndex, googleMapInstance, markersPositions);
  }

  return startingIndex;
}


//Divide un unico FeatureCollection de tipo MultiPolygon en múltiples Features individuales
export function splitMultiPolygonToFeatures(
  originalFC: MyFC
): MyFC {
  const turfResult = flatten(originalFC as any) as any;


  const newFeatures: MyFeature[] = (turfResult.features as any[]).map((feat, idx) => {
    return {
      type: 'Feature',
      id: (feat.id !== undefined ? String(feat.id) : getUniqueId()) + `-${idx}`,
      properties: {
        ...feat.properties,
        customId: getUniqueId()
      },
      geometry: feat.geometry as MyGeometry
    };
  });

  return {
    type: 'FeatureCollection',
    features: newFeatures
  };
}


//Creo un marcador con un label que contiene el índice del polígono
function createLabelMarker(
  center: google.maps.LatLng,
  index: number,
  map: google.maps.Map,
  markersPositions: google.maps.marker.AdvancedMarkerElement[]
): number {
  const labelElement = document.createElement('div');
  labelElement.style.background = 'white';
  labelElement.style.border = '1px solid black';
  labelElement.style.borderRadius = '4px';
  labelElement.style.padding = '2px 6px';
  labelElement.style.fontSize = '12px';
  labelElement.style.fontWeight = 'bold';
  labelElement.style.color = 'black';
  labelElement.innerText = String(index + 1);

  const marker = new google.maps.marker.AdvancedMarkerElement({
    map,
    position: center,
    content: labelElement,
  });

  markersPositions.push(marker);
  return index + 1;
}


export function createBounds(bounds: Bounds | null) {
  if (!bounds) return null;
  const { nw, se } = bounds;
  const sw = new google.maps.LatLng(se[1], nw[0]);
  const ne = new google.maps.LatLng(nw[1], se[0]);
  return new google.maps.LatLngBounds(sw, ne);
}


export function normalizeFeatureCollection(featureCollection: any): FeatureCollection {
  const features: any[] = [];

  featureCollection.features.forEach((feature: any) => {
    const splitFeatures = splitMultiPolygonFeature(feature);
    features.push(...splitFeatures);
  });

  return {
    type: 'FeatureCollection',
    features,
  };
}

function splitMultiPolygonFeature(feature: any): any[] {
  if (feature.geometry.type !== 'MultiPolygon') {
    return [feature];
  }

  const parentId = feature.id || feature.properties.customId || feature.properties.id;
  
  return feature.geometry.coordinates.map((polygonCoords: any, index: number) => ({
    type: 'Feature',
    properties: {
      ...feature.properties,
      customId: getUniqueId(), // Each split feature needs its own unique ID
      parentId: parentId, // Keep reference to original feature
      polygonIndex: index,
    },
    geometry: {
      type: 'Polygon',
      coordinates: polygonCoords,
    },
  }));
}